import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, CaretDown, DownloadSimple, Heart, ImageSquare } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { resolveMediaUrl } from '../../../utils/media';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent } from '../../../components/ui/dialog';
import { guestService } from '../services/guestService';
import { toast } from 'sonner';
import { useEventTheme } from '../../../context/ThemeContext';
import { colorWithAlpha, createMutedPalette } from '../../../utils/mediaPalette';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const PIN_ROTATIONS = [-2.2, 1.6, -1.1, 2.4, -1.7, 1.1, -2.6, 1.9];

export default function LivePhotoWall({ event }) {
  const { theme } = useEventTheme();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [likedPhotos, setLikedPhotos] = useState(() => new Set());
  const [showScrollHint, setShowScrollHint] = useState(false);
  const photoGridRef = useRef(null);
  const wall = event?.guest_experience?.photo_wall;
  const uploadToken = wall?.sources?.find((source) => source.enabled !== false)?.token || wall?.public_token;
  const wallPalette = useMemo(
    () => createMutedPalette(theme.colors.primary, theme.colors.secondary),
    [theme.colors.primary, theme.colors.secondary]
  );
  const pinColors = [
    theme.colors.secondary,
    theme.colors.primary,
    theme.colors.accent,
    theme.colors.muted,
  ];

  const fetchFeed = useCallback(async () => {
    if (!event?.id || wall?.enabled === false) {
      setLoading(false);
      return;
    }
    try {
      const feed = await guestService.getPhotoWallFeed(event.id);
      setPhotos(feed);
    } catch (error) {
      console.error('Failed to load live photo wall:', error);
    } finally {
      setLoading(false);
    }
  }, [event?.id, wall?.enabled]);

  useEffect(() => {
    fetchFeed();
    const timer = window.setInterval(fetchFeed, 12000);
    return () => window.clearInterval(timer);
  }, [fetchFeed]);

  useEffect(() => {
    if (!uploadToken || wall?.enabled === false || !window.EventSource) return undefined;
    const source = new window.EventSource(`${BACKEND_URL}/api/photo-wall/${uploadToken}/stream`);
    const onPhotos = (message) => {
      try {
        setPhotos(JSON.parse(message.data));
        setLoading(false);
      } catch (error) {
        console.warn('Invalid photo wall update:', error);
      }
    };
    source.addEventListener('photos', onPhotos);
    source.onerror = () => {
      source.close();
    };
    return () => source.close();
  }, [uploadToken, wall?.enabled]);

  const categories = useMemo(() => {
    const itinerary = new Map((event?.itinerary || []).map((item) => [item.id, item.name]));
    return photos.map((photo) => ({
      ...photo,
      category_name: itinerary.get(photo.itinerary_item_id),
    }));
  }, [event?.itinerary, photos]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const container = photoGridRef.current;
      setShowScrollHint(Boolean(
        container && container.scrollHeight > container.clientHeight + 12
      ));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [categories.length]);

  const handlePhotoGridScroll = (event) => {
    const container = event.currentTarget;
    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollHint(remaining > 16);
  };

  const toggleLike = async (photo) => {
    try {
      const like = await guestService.togglePhotoWallLike(event.id, photo.id);
      setPhotos((current) => current.map((item) => (
        item.id === photo.id ? { ...item, like_count: like.like_count } : item
      )));
      setLikedPhotos((current) => {
        const next = new Set(current);
        if (like.liked) next.add(photo.id);
        else next.delete(photo.id);
        return next;
      });
    } catch (error) {
      toast.error('Could not update your favorite');
    }
  };

  const downloadPhoto = async (photo) => {
    try {
      const response = await fetch(resolveMediaUrl(photo.url, { preferProxy: true }));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `event-photo-${photo.id}.jpg`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Could not download this photo');
    }
  };

  return (
    <section
      id="photo-wall"
      className="px-1 py-6 md:px-2 md:py-8"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-wedding-charcoal/45">Live Photo Wall</p>
          <h2 className="mt-2 font-serif text-3xl text-wedding-charcoal">The celebration through everyone&apos;s eyes</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-wedding-charcoal/65">
            Take a photo and watch automatically moderated moments from every table appear here live.
          </p>
        </div>
        {uploadToken && wall?.enabled !== false && wall?.upload_enabled !== false && (
          <Button asChild className="rounded-full bg-wedding-sage px-6 hover:bg-wedding-sage/90">
            <Link to={`/share/${uploadToken}`} target="_blank" rel="noopener noreferrer">
              <Camera className="mr-2 h-5 w-5" /> Take a photo
            </Link>
          </Button>
        )}
      </div>

      {wall?.enabled === false ? (
        <div className="mt-8 rounded-[1.5rem] border border-dashed border-border p-10 text-center text-wedding-charcoal/60">
          The live photo wall is currently paused.
        </div>
      ) : loading ? (
        <div
          className="mx-auto mt-8 w-full rounded-[1.6rem] border-[6px] p-4 shadow-[inset_0_0_30px_rgba(0,0,0,0.12),0_18px_36px_rgba(0,0,0,0.12)] md:border-[9px] md:p-6"
          style={{
            borderColor: theme.colors.primary,
            backgroundColor: wallPalette.soft,
            backgroundImage: `radial-gradient(circle at 2px 2px, ${colorWithAlpha(theme.colors.primary, 0.16)} 1px, transparent 1.5px), linear-gradient(135deg, rgba(255,255,255,0.18), ${colorWithAlpha(theme.colors.secondary, 0.12)})`,
            backgroundSize: '16px 16px, 100% 100%',
          }}
        >
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="aspect-[4/5] animate-pulse bg-card p-2 shadow-lg">
                <div className="h-full bg-white/35" />
              </div>
            ))}
          </div>
        </div>
      ) : categories.length ? (
        <div
          className="relative mx-auto mt-8 w-full max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-[1.6rem] border-[6px] px-3 pb-3 pt-6 shadow-[inset_0_0_34px_rgba(0,0,0,0.14),0_18px_36px_rgba(0,0,0,0.13)] md:max-w-none md:border-[9px] md:px-8 md:pt-10"
          style={{
            borderColor: theme.colors.primary,
            backgroundColor: wallPalette.soft,
            backgroundImage: `radial-gradient(circle at 2px 2px, ${colorWithAlpha(theme.colors.primary, 0.18)} 1px, transparent 1.5px), linear-gradient(135deg, rgba(255,255,255,0.2), ${colorWithAlpha(theme.colors.secondary, 0.14)})`,
            backgroundSize: '16px 16px, 100% 100%',
          }}
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-black/15 to-transparent" />
          <div
            ref={photoGridRef}
            onScroll={handlePhotoGridScroll}
            className="grid max-h-[68svh] grid-cols-2 gap-x-3 gap-y-7 overflow-y-auto overflow-x-hidden px-1 pb-10 pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:max-h-[42rem] md:grid-cols-3 md:gap-x-7 md:px-2 md:pb-2 lg:grid-cols-4"
          >
            {categories.map((photo, index) => (
              <article
                key={photo.id}
                className="group relative flex w-full flex-col bg-card p-1.5 pb-2 shadow-[0_12px_24px_rgba(0,0,0,0.22)] transition-shadow duration-300 hover:z-10 hover:shadow-[0_18px_34px_rgba(0,0,0,0.3)] md:h-[19rem] md:p-2 md:pb-2.5 lg:h-[20rem]"
                style={{
                  transform: `rotate(${PIN_ROTATIONS[index % PIN_ROTATIONS.length]}deg)`,
                }}
              >
                <span
                  className="pointer-events-none absolute -top-2 left-1/2 z-20 h-4 w-4 -translate-x-1/2 rounded-full border border-white/45 shadow-[0_3px_5px_rgba(0,0,0,0.35)]"
                  style={{
                    background: `radial-gradient(circle at 35% 30%, #fff7 0%, transparent 30%), ${pinColors[index % pinColors.length]}`,
                  }}
                  aria-hidden="true"
                />
                <span
                  className="pointer-events-none absolute left-1/2 top-2 h-2 w-5 -translate-x-1/2 rounded-full bg-black/15 blur-sm"
                  aria-hidden="true"
                />
                <button
                  type="button"
                  onClick={() => setSelectedPhoto(photo)}
                  className="relative block aspect-[4/5] w-full overflow-hidden bg-muted md:min-h-0 md:flex-1 md:aspect-auto"
                >
                  <img
                    src={resolveMediaUrl(photo.url, { preferProxy: true })}
                    alt={photo.category_name || 'Live guest moment'}
                    className="absolute inset-0 h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.035] md:object-cover"
                  />
                </button>
                <div className="mt-2 flex items-center justify-between gap-1 px-1">
                  <div className="min-w-0 text-left">
                    <p className="truncate font-serif text-sm text-wedding-charcoal">
                      <span className="md:hidden">{photo.category_name || ''}</span>
                      <span className="hidden md:inline">{photo.category_name || photo.location_label || ''}</span>
                    </p>
                    {photo.location_label && photo.category_name && (
                      <p className="hidden truncate text-[0.625rem] uppercase tracking-[0.12em] text-muted-foreground md:block">
                        {photo.location_label}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full"
                      onClick={() => toggleLike(photo)}
                    >
                      <Heart className={`h-4 w-4 ${likedPhotos.has(photo.id) ? 'fill-rose-500 text-rose-500' : ''}`} />
                    </Button>
                    <span className="text-[0.6875rem] text-muted-foreground">{photo.like_count || 0}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 rounded-full"
                      onClick={() => downloadPhoto(photo)}
                    >
                      <DownloadSimple className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
          {showScrollHint && (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex h-16 items-end justify-center pb-2 md:hidden"
              style={{ background: `linear-gradient(to top, ${wallPalette.soft}, ${colorWithAlpha(wallPalette.soft, 0.85)}, transparent)` }}
            >
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/90 px-3 py-1.5 text-[0.5625rem] font-medium uppercase tracking-[0.2em] text-foreground shadow-sm backdrop-blur">
                More moments
                <CaretDown className="h-3.5 w-3.5 animate-bounce" />
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 rounded-[1.5rem] border border-dashed border-border p-10 text-center text-wedding-charcoal/60">
          <ImageSquare className="mx-auto h-10 w-10 opacity-50" />
          <p className="mt-3">Be the first guest to add a live moment.</p>
        </div>
      )}

      <Dialog open={Boolean(selectedPhoto)} onOpenChange={(open) => !open && setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl border-0 bg-black/95 p-2">
          {selectedPhoto && (
            <img
              src={resolveMediaUrl(selectedPhoto.url, { preferProxy: true })}
              alt="Guest moment"
              className="max-h-[85vh] w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
